import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { uploadFiles } from "./Attachments";
import { Paperclip, X } from "lucide-react";

const ACCEPT = "image/*,.pdf,.csv,.xls,.xlsx,.txt,.doc,.docx";

const Pills = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5">{Object.entries(options).map(([k, l]) => <button type="button" key={k} data-testid={`${testId}-${k}`} onClick={() => onChange(k)} className={`rounded-full border px-2.5 py-1 text-xs ${value === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:border-blue-400"}`}>{l}</button>)}</div>
);

export function NewTicketDialog({ open, meta, onClose, onCreated }) {
  const loc = useLocation();
  const [f, setF] = useState({ subject: "", description: "", category: "problema", priority: "normale" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tickets", { ...f, page: loc.pathname });
      if (files.length) await uploadFiles(r.data.id, files);
      toast.success(`Richiesta #${r.data.number} inviata: riceverai conferma via email`);
      setF({ subject: "", description: "", category: "problema", priority: "normale" }); setFiles([]); onCreated(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ticket-dialog">
        <DialogHeader><DialogTitle>Nuova richiesta di assistenza</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Oggetto</Label><Input data-testid="ticket-subject-input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="es. Il report settimanale non arriva" /></div>
          <div className="space-y-1"><Label>Tipo</Label><Pills options={meta.categories} value={f.category} onChange={(v) => setF({ ...f, category: v })} testId="ticket-category" /></div>
          <div className="space-y-1"><Label>Priorità</Label><Pills options={meta.priorities} value={f.priority} onChange={(v) => setF({ ...f, priority: v })} testId="ticket-priority" /></div>
          <div className="space-y-1"><Label>Descrizione</Label><Textarea data-testid="ticket-description-input" rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Cosa succede, da quando, su quale pagina o circuito, eventuale messaggio di errore..." /></div>
          <div className="space-y-1"><Label>Allegati (screenshot, PDF, Excel · max 10 MB ciascuno)</Label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:border-blue-400" data-testid="ticket-new-attach-label">
              <Paperclip className="h-3.5 w-3.5" />{files.length ? `${files.length} file selezionat${files.length > 1 ? "i" : "o"} — aggiungi altri` : "Scegli file o trascina qui"}
              <input type="file" multiple accept={ACCEPT} className="hidden" data-testid="ticket-new-attach-input" onChange={(e) => { setFiles([...files, ...e.target.files].slice(0, 10)); e.target.value = ""; }} />
            </label>
            {files.length > 0 && <div className="flex flex-wrap gap-1.5">{files.map((x, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px]" data-testid={`ticket-new-file-${i}`}>{x.name.slice(0, 28)}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button></span>)}</div>}
          </div>
          <p className="text-xs text-slate-500">La richiesta viene inviata a <b>{meta.support_email}</b>; riceverai una conferma e gli aggiornamenti di stato via email.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button data-testid="ticket-submit" className="bg-blue-600 hover:bg-blue-700" disabled={busy || f.subject.trim().length < 3 || f.description.trim().length < 5} onClick={submit}>{busy ? "Invio..." : "Invia richiesta"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
